import Breadcrumbs from "@/components/Breadcrumbs";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { VersionManagementService } from "@/services/admin-services";
import { Version, VersionStatus } from "@/services/admin-services/models";
import {
    Alert,
    alpha,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Content",
        to: "../..",
    },
    {
        title: "Versions",
        to: "",
    },
];

const Versions = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<Version[]>([]);
    const [deleteValue, setDeleteValue] = useState<Version | null>(null);

    const [createVersion, setCreateVersion] = useState<boolean>(false);
    const [versionName, setVersionName] = useState<string>("");
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [status, setStatus] = useState<VersionStatus>();

    const [renameValue, setRenameValue] = useState<Version | null>(null);
    const [renameName, setRenameName] = useState<string>("");
    const [isRenaming, setIsRenaming] = useState<boolean>(false);
    const [renameError, setRenameError] = useState<string | null>(null);

    const theme = useTheme();

    const versionService = useMemo(() => new VersionManagementService(), []);

    const timerRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        versionService.getCreateStatus().then(setStatus);
        versionService.get().then((data) => {
            setData(data);
            setIsLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!status) return;

        setCreateVersion(status.status === "Running");
        setIsCreating(status.status === "Running");

        if (status.status === "Running") {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                versionService.getCreateStatus().then(setStatus);
            }, 10000);
        }
    }, [status, versionService]);

    const renderStatus = (active: boolean) => {
        let statusColor = theme.palette.success;
        if (!active) statusColor = theme.palette.error;

        if (!active) return null;
        return (
            <Typography
                variant={"caption"}
                fontWeight={700}
                sx={{
                    bgcolor: alpha(statusColor.light, 0.1),
                    color: statusColor.dark,
                    paddingX: 1.5,
                    paddingY: 0.5,
                    borderRadius: 4,
                    display: "inline",
                    ml: 1,
                }}
            >
                Current default version
            </Typography>
        );
    };

    const onDelete = (version: Version) => setDeleteValue(version);

    const onCancelDelete = () => setDeleteValue(null);

    const onPerformDelete = () => {
        if (deleteValue) {
            versionService.deleteVersion(deleteValue.id).then((success) => {
                if (success) setData(data.filter((item) => item.id !== deleteValue.id));
                setDeleteValue(null);
            });
        }
    };

    const onRename = (version: Version) => {
        setRenameValue(version);
        setRenameName(version.name);
        setRenameError(null);
    };

    const onCancelRename = () => {
        if (isRenaming) return;
        setRenameValue(null);
    };

    const onRenameSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!renameValue) return;

        const trimmedName = renameName.trim();
        if (!trimmedName) {
            setRenameError("Please enter a name.");
            return;
        }

        // Renaming a version to the name it already has is a no-op - nothing to send.
        if (trimmedName.toLowerCase() === renameValue.name.toLowerCase()) {
            setRenameValue(null);
            return;
        }

        const isTaken = data.some(
            (item) => item.id !== renameValue.id && item.name.toLowerCase() === trimmedName.toLowerCase()
        );
        if (isTaken) {
            setRenameError("That name is already in use by another version.");
            return;
        }

        setRenameError(null);
        setIsRenaming(true);

        versionService.renameVersion(renameValue.id, trimmedName).then((updated) => {
            setIsRenaming(false);

            if (!updated) {
                setRenameError("That name is already in use by another version.");
                return;
            }

            setData(data.map((item) => (item.id === updated.id ? updated : item)));
            setRenameValue(null);
        });
    };

    const onCreate = () => {
        setCreateVersion(true);
        setVersionName("");
    };

    const onCancelCreate = () => {
        if (isCreating) return;
        setCreateVersion(false);
    };

    const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsCreating(true);

        versionService.createVersion(versionName).then(() => {
            versionService.getCreateStatus().then(setStatus);
        });
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex" }}>
                    <Button variant={"contained"} color={"primary"} onClick={onCreate} sx={{ ml: "auto" }}>
                        Create new version
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Version
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 200 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Publication Date
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 100 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data
                                        .sort((a, b) => b.publicationDate.getTime() - a.publicationDate.getTime())
                                        .map((item, i) => (
                                            <TableRow
                                                key={i}
                                                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                        {item.name}
                                                        {renderStatus(item.isDefault)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell scope="row">
                                                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                        {item.publicationDate.toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell scope="row" sx={{ p: 0 }}>
                                                    <Box sx={{ display: "flex" }}>
                                                        <Button sx={{ ml: "auto" }} onClick={() => onRename(item)}>
                                                            Rename
                                                        </Button>
                                                        <Button
                                                            color={"error"}
                                                            sx={{ mr: 1 }}
                                                            onClick={() => onDelete(item)}
                                                            disabled={item.isDefault}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {createVersion && (
                <Dialog open={createVersion} onClose={onCancelCreate}>
                    {isCreating ? (
                        <DialogContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    placeContent: "center",
                                    flexDirection: "column",
                                    width: 300,
                                    height: 200,
                                    alignItems: "center",
                                }}
                            >
                                <CircularProgress />
                                <Typography variant={"subtitle2"} sx={{ mt: 4 }}>
                                    Creating version...
                                </Typography>
                                {status?.message && (
                                    <Typography
                                        variant={"subtitle2"}
                                        sx={{ whiteSpace: "pre-wrap", textAlign: "center", fontWeight: 400, mt: 2 }}
                                    >
                                        {status.message.replace("  ", "\r\n")}
                                    </Typography>
                                )}
                            </Box>
                        </DialogContent>
                    ) : (
                        <form onSubmit={(event) => onFormSubmit(event)}>
                            <DialogTitle>Create new version</DialogTitle>
                            <DialogContent dividers>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            value={versionName}
                                            onChange={(event) => setVersionName(event.target.value)}
                                            label={`Version`}
                                            type="text"
                                            variant="outlined"
                                            disabled={isCreating}
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <Alert severity="warning">
                                <Typography variant={"caption"}>
                                    Creating a new version will take around 15 minutes, once a version has been created
                                    it will immediatly be published and cannot be changed anymore.
                                </Typography>
                            </Alert>
                            <Divider />
                            <DialogActions>
                                <Button onClick={onCancelCreate} disabled={isCreating}>
                                    Cancel
                                </Button>
                                <Button variant="contained" type="submit" disabled={isCreating}>
                                    Create new version
                                </Button>
                            </DialogActions>
                        </form>
                    )}
                </Dialog>
            )}

            {renameValue && (
                <Dialog open={Boolean(renameValue)} onClose={onCancelRename}>
                    <form onSubmit={onRenameSubmit}>
                        <DialogTitle>Rename version</DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        value={renameName}
                                        onChange={(event) => {
                                            setRenameName(event.target.value);
                                            setRenameError(null);
                                        }}
                                        label={`Version`}
                                        type="text"
                                        variant="outlined"
                                        disabled={isRenaming}
                                        error={Boolean(renameError)}
                                        helperText={renameError}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        {renameValue.isDefault && (
                            <Alert severity="warning">
                                <Typography variant={"caption"}>
                                    This is the current default version. Renaming it does not change what&apos;s
                                    live &mdash;&nbsp;it stays the default version under its new name.
                                </Typography>
                            </Alert>
                        )}
                        <Divider />
                        <DialogActions>
                            <Button onClick={onCancelRename} disabled={isRenaming}>
                                Cancel
                            </Button>
                            <Button variant="contained" type="submit" disabled={isRenaming}>
                                {isRenaming ? <CircularProgress size={20} /> : "Rename"}
                            </Button>
                        </DialogActions>
                    </form>
                </Dialog>
            )}

            {deleteValue && (
                <ConfirmDeleteDialog
                    open={Boolean(deleteValue)}
                    title={"Delete version"}
                    message={`Are you sure you wish to delete the version ${deleteValue.name}? You can not undo this action!`}
                    onCancel={onCancelDelete}
                    onDelete={onPerformDelete}
                />
            )}
        </Box>
    );
};

export default Versions;
